import React from 'react';
import { Helmet } from 'react-helmet';

import Link from '../../shared/components/hyperlink';
import JsonLd from '../../shared/components/json-ld';
import CopyToClipboard from '../../shared/components/copy-to-clipboard';
import { withError } from '../../shared/components/error-boundary';
import AdaptiveImage from '../../shared/components/adaptive-image';
import RichContent from '../../utility/components/rich-content';
import RightRail from './right-rail';
import BlogAdBanner from './right-rail/ad-banner';
import ContentCarouselContainer from './content-carousel/content-carousel-container';
import ShareActionButton from '../../social/share-action-button';
import ShareActionBar from '../../social/share-action-bar';
import { Helpers } from '../../core/src/helpers';
import Cta from './cta';
import Disclaimer from '../components/disclaimer';
import { schemaModifiers } from '../components/utils';
import SEOLinks from '../../page/components/seo-links/seo-links';
import AboveTheFoldOnlyServerRender from '../../shared/components/above-the-fold-only-server-render';
import Button from '../../ui/components/button';
import { Grid, Column } from '../../ui/components/grid';

import '../css/blog.less';
import '../css/blog-v2.less';
import '../css/blog-shared.less';

const DEFAULT_SHARE_BUTTONS = [
    {
        type: 'Facebook',
        utm_source: 'tech_takes',
        utm_content: 'facebook',
    },
    {
        type: 'LinkedIn',
        utm_source: 'tech_takes',
        utm_content: 'linkedin',
    },
    {
        type: 'Twitter',
        utm_source: 'tech_takes',
        utm_content: 'twitter',
    },
    {
        type: 'Reddit',
        utm_source: 'tech_takes',
        utm_content: 'reddit',
    },
];

const initialState = {
    header: 'HP TECH TAKES /...',
    subHeader: "Exploring today's technology for tomorrow's possibilities",
};

const searchFilters = {
    template_key: 'blog',
    hosted: 'true',
    blog_archive: 'false',
};

export const BlogTitleBar = ({ blogDate, authorName, blogTitle }) => {
    let date = Helpers.date.toDateString(new Date(blogDate));

    return (
        <React.Fragment>
            <h1 className="blog-title"> {blogTitle} </h1>
            <div className="blog-date-and-author">
                {authorName.trim().length > 0 && (
                    <div style={{ display: 'inline-block', marginRight: '8px' }}>{authorName}</div>
                )}
                {authorName.trim().length > 0 && <div style={{ display: 'inline-block', marginRight: '8px' }}>|</div>}
                {blogDate && (
                    <div style={{ display: 'inline-block' }} className="article-date">
                        {date}
                    </div>
                )}
            </div>
        </React.Fragment>
    );
};

class Blog extends React.Component {
    processSearchBoxResponse = (response = {}) => {
        return Object.keys(response).length
            ? response.results.map(result => {
                  const { blog_title, vanity_url, blog_thumbnail, blog_date, blog_content, id, description } = result;
                  return {
                      blogTitle: blog_title.raw,
                      vanityUrl: vanity_url.raw,
                      blogThumbnail: JSON.parse(blog_thumbnail.raw),
                      blogDate: blog_date.raw,
                      blogContent: blog_content.raw,
                      description: description.raw,
                      document_id: id.raw,
                  };
              })
            : [];
    };

    getSchemaModifiers = () => {
        const { categories, tags } = this.props;
        if (!(categories instanceof Array)) return [];
        let contentModifiers = [],
            blockModifiers = [],
            entityModifiers = [],
            dataAggregators = [];
        categories.forEach(cat => {
            if (schemaModifiers[cat]) {
                let { blockModifier, contentModifier, entityModifier, dataAggregator } = schemaModifiers[cat]();
                dataAggregators.unshift(dataAggregator);
                contentModifiers.unshift(contentModifier);
                blockModifiers.unshift(blockModifier);
                entityModifiers.unshift(entityModifier);
            }
        });

        // used for determining where to put the ad banner.
        // this applies to all blogs
        dataAggregators.push(parsed => {
            let paragraphCount = 0;
            try {
                let entityMap = parsed && parsed.entityMap;
                let foundBlogAdPlacement = false;
                parsed.blocks.forEach((block, index) => {
                    if ((block.type === 'unstyled' || block.type === 'remove-all-styles') && block.text.length > 0) {
                        paragraphCount++;
                    }
                    if (!foundBlogAdPlacement && (paragraphCount === 2 || index === parsed.blocks.length - 1)) {
                        block.data = block.data || {};
                        block.data.nestedComponent = (
                            <div className="blog-ad-mobile blog-ad-tech-takes-article-view">
                                <BlogAdBanner
                                    bannerMeta={{ categories, tags }}
                                    type="mobile"
                                    device="mobile"
                                    breakpoint="blog-v2-article"
                                />
                            </div>
                        );
                        foundBlogAdPlacement = true;
                    }

                    // TEMP FIX: NEED TO REMOVE THE PRODUCT CAROUSEL HEADERS, AND ADD IT ON TO CAROUSEL ENTITY
                    if (/header/.test(block.type)) {
                        let nextBlock = parsed.blocks[index + 1];
                        let nextEntity =
                            nextBlock && nextBlock.entityRanges instanceof Array
                                ? nextBlock.entityRanges.find(e => e.offset === 0)
                                : null;
                        let entity = nextEntity ? entityMap[nextEntity.key] : null;
                        if (entity && ['PRODUCTCAROUSEL', 'DYNAMICPRODUCTCAROUSEL'].includes(entity.type)) {
                            entity.data.header = block.text;
                            block.text = '';
                        }
                    }
                });
            } catch (e) {}
        });

        return { contentModifiers, blockModifiers, entityModifiers, dataAggregators };
    };

    renderCSS = css => <style dangerouslySetInnerHTML={Helpers.createMarkup(css)} />;

    render() {
        const {
            blog = {},
            author = {},
            url = '',
            header,
            subHeader,
            tags = [],
            categories = [],
            _id,
            vanityUrl,
            css,
            wordCloud,
            updatedAt,
            blogKey,
            blogConfig,
        } = this.props;
        let {
            blogTitle = '',
            blogContent = '',
            blogThumbnail: image = {},
            blogDate = '',
            hideCta = false,
            blogArchive,
        } = blog;
        const { authorName = '' } = author;
        const { subscribeCta } = blogConfig || {};
        const { links: subscribeLinks, linkType: subscribeLinkType } = subscribeCta || {};

        if (typeof image === 'string') {
            image = {
                src: image,
                alt: 'HPImage',
                title: 'HPImage',
            };
        } else if (Array.isArray(image)) {
            image = {
                src: image[0],
                alt: 'HPImage',
                title: 'HPImage',
            };
        } else if (!image) {
            image = {
                src: null,
                alt: 'HPImage',
                title: 'HPImage',
            };
        }

        //For JSONLD
        let articleBody = '';
        const blocks = blogContent ? JSON.parse(blogContent).blocks : [];
        for (let block of blocks) {
            articleBody += block.text;
        }

        let dateModified =
            typeof updatedAt === 'string'
                ? updatedAt
                : updatedAt && typeof updatedAt.toISOString === 'function'
                ? updatedAt.toISOString()
                : blogDate;
        let data = {
            headline: blogTitle,
            image: image.src,
            url,
            datePublished: blogDate,
            dateModified,
            articleBody,
            author: {
                '@type': 'Person',
                name: authorName || 'HP',
            },
        };

        const { contentModifiers, blockModifiers, entityModifiers, dataAggregators } =
            this.getSchemaModifiers(vanityUrl) || {};

        const getLink = () => (typeof window !== 'undefined' ? window.location.href : '');

        let carouselComponent = tags.length > 0 && (
            <AboveTheFoldOnlyServerRender skip={true} showTimeout={2000}>
                <ContentCarouselContainer header={'Recommended articles'} tags={tags} />
            </AboveTheFoldOnlyServerRender>
        );

        return (
            <div className={`blog-page v2`}>
                <Helmet>
                    <meta charSet="utf-8" />
                    <title>{this.props.title}</title>
                    <link rel="canonical" href={this.props.canonical} />
                    <meta name="description" content={this.props.description} />
                    <meta name="keywords" content={this.props.keywords} />

                    <meta property="og:title" content={this.props.title} />
                    <meta property="og:url" content={this.props.canonical} />
                    <meta property="og:description" content={this.props.description} />
                    <meta property="og:image" content={image.src} />

                    <meta name="twitter:card" content="summary_large_image" />
                    <meta name="twitter:site" value="HP® Tech Takes" />
                    <meta name="twitter:title" content={this.props.title} />
                    <meta name="twitter:description" content={this.props.description} />
                    <meta name="twitter:image" content={image.src} />

                    {this.props.robots && <meta name="robots" content="noindex" />}
                </Helmet>
                <Grid className="blog-grid">
                    <Column
                        columnClass="allow-overflow blog-header-column"
                        xl={12}
                        large={12}
                        medium={12}
                        small={8}
                        xs={4}
                        xxs={4}
                    >
                        <div className="hp-tech-takes-header">
                            <div className="hp-tech-takes-title">
                                <h2 className={`tech-takes-header${header === initialState.header ? ' hp-blue' : ''}`}>
                                    {header}
                                </h2>
                                <div className="tech-takes-subheader">{subHeader}</div>
                            </div>
                        </div>
                    </Column>
                    <Column
                        columnClass="allow-overflow blog-content-column"
                        xl={9}
                        large={9}
                        medium={8}
                        small={8}
                        xs={4}
                        xxs={4}
                    >
                        <div className={`blog-content-container${blog.blogArchive ? ' blog-archive' : ''}`}>
                            <div
                                style={{
                                    maxWidth: '1250px',
                                    paddingRight: '0px',
                                    display: 'inline-block',
                                    verticalAlign: 'top',
                                    width: '100%',
                                }}
                            >
                                <div className="blog-header-image-container">
                                    <Cta tags={tags} hideCta={hideCta} />
                                    <AdaptiveImage
                                        className="blog-header-image"
                                        maxWidth={1000}
                                        src={image.src}
                                        alt={image.alt}
                                        title={image.title}
                                    />
                                </div>
                                <div className="wysiwyg-link-container">
                                    <Link className="wysiwyg-link home" to={'/tech-takes'}>
                                        <Button type="secondary" action="back" text="Home" size="small" />
                                    </Link>
                                </div>
                                <ShareActionBar>
                                    {!!blogTitle &&
                                        DEFAULT_SHARE_BUTTONS.map((share, index) => {
                                            return (
                                                <ShareActionButton
                                                    key={index}
                                                    type={share.type}
                                                    title={blogTitle}
                                                    text={share.text}
                                                    extraText={share.extraText}
                                                    utm_source={share.utm_source}
                                                    utm_content={share.utm_content}
                                                />
                                            );
                                        })}
                                    <ShareActionButton
                                        type="Email"
                                        title={blogTitle}
                                        text="Checkout this article from HP Tech Takes"
                                        extraText={
                                            'Just wanted to share this interesting article called "' +
                                            blogTitle +
                                            '". Here\'s the link: ' +
                                            getLink()
                                        }
                                        utm_source="tech_takes"
                                        utm_content="email_link"
                                    />
                                    <CopyToClipboard
                                        title={blogTitle}
                                        utm_source="tech_takes"
                                        utm_content="copy_link"
                                    />
                                </ShareActionBar>
                                <BlogTitleBar blogDate={blogDate} blogTitle={blogTitle} authorName={authorName} />
                                {blogContent && (
                                    <RichContent
                                        blogArchive={blogArchive}
                                        design="v2"
                                        raw={blogContent}
                                        overrideNewTab={true}
                                        lazyLoadImages={true}
                                        imageMaxWidth={1000}
                                        entityModifiers={entityModifiers}
                                        blockModifiers={blockModifiers}
                                        contentModifiers={contentModifiers}
                                        dataAggregators={dataAggregators}
                                        vanityUrl={vanityUrl}
                                        blogKey={blogKey}
                                    />
                                )}
                            </div>
                            {carouselComponent}
                            <JsonLd data={data} type="blog" />
                            {css && this.renderCSS(css)}
                        </div>
                    </Column>
                    <Column columnClass="blog-right-rail-column" xl={3} large={3} medium={4} small={8} xs={4} xxs={4}>
                        <RightRail
                            bannerMeta={{ categories, tags }}
                            tagsHeader="Related tags"
                            tags={tags}
                            page={'tech-takes-article-view'}
                            subscribeLinkType={subscribeLinkType}
                            subscribeLinkId="blog-article-view:subscribe"
                            subscribeHref={subscribeLinks.BLOG}
                        />
                    </Column>
                    <Column xl={12} large={12} medium={12} small={8} xs={4} xxs={4}>
                        {wordCloud && wordCloud.length > 0 && (
                            <div className="blog-footer-links">
                                <SEOLinks links={wordCloud} useDefaultLinks={true} className="v2" />
                            </div>
                        )}
                    </Column>
                    <div className="blog-disclaimer-section">
                        <Disclaimer />
                    </div>
                </Grid>
            </div>
        );
    }
}

export default withError(Blog);
